/** The item views. Every number here is transcribed from the Android layout named in
 *  the comment — see spec §2. Plain elements and inline styles rather than MUI: this
 *  tree renders inside the preview frame and must not inherit the console's design
 *  system.
 */
import { useState } from 'react';

import type { Item, SliderImage } from '../mappers/activityDetails';
import { androidHtml } from '../text/androidHtml';
import { imageUrl } from '../images';
import { BODY_MARGINS, SECTION_MARGINS, marginStyle } from './margins';

export interface ItemContext {
  imagesBase: string;
  imageWidthPx: number;
}

/** bg_uikit_image_placeholder_comming_soon: #E7E7E7 with the artwork inset 56dp. */
function Placeholder({ height }: { height: number }) {
  return (
    <div
      style={{
        height,
        background: 'var(--and-color-surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--and-color-dark-disabled)',
        fontSize: 12,
      }}
    >
      Coming soon
    </div>
  );
}

/** item_image_slider.xml -> ImageSliderView, 312dp, PagerSnapHelper paging.
 *  The n/m counter is `bottom|end`, 10dp margins, black-80 pill, minWidth 60dp. */
function ImageSlider({ images, ctx }: { images: SliderImage[]; ctx: ItemContext }) {
  const [page, setPage] = useState(0);
  const height = 312;
  const current = images[Math.min(page, images.length - 1)];

  return (
    <div style={{ position: 'relative', height, overflow: 'hidden' }}>
      {current?.placeholder || !current?.image ? (
        <Placeholder height={height} />
      ) : (
        <img
          src={imageUrl(ctx.imagesBase, current.image, ctx.imageWidthPx) ?? undefined}
          alt=""
          style={{ width: '100%', height, objectFit: 'cover' }}
        />
      )}

      {/* item_image_overlay.xml: 60x60 centred, bg_button_circle, padding 18dp */}
      {current?.overlayPlay && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'var(--and-color-black-80)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--and-color-white)',
              fontSize: 24,
            }}
          >
            ▶
          </div>
        </div>
      )}

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            style={{ ...edgeTap, left: 0 }}
          />
          <button
            type="button"
            aria-label="Next image"
            onClick={() => setPage((p) => Math.min(images.length - 1, p + 1))}
            style={{ ...edgeTap, right: 0 }}
          />
          <div
            style={{
              position: 'absolute',
              right: 10,
              bottom: 10,
              minWidth: 60,
              padding: '4px 10px',
              borderRadius: 17,
              background: 'var(--and-color-black-80)',
              color: 'var(--and-color-white)',
              fontSize: 12,
              textAlign: 'center',
            }}
          >
            {`${Math.min(page, images.length - 1) + 1} / ${images.length}`}
          </div>
        </>
      )}
    </div>
  );
}

const edgeTap: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 64,
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
};

/** item_details_header.xml */
function DetailsHeader({
  topLabel,
  title,
  descriptionHtml,
}: {
  topLabel: string | null;
  title: string;
  descriptionHtml: string | null;
}) {
  return (
    <div style={{ paddingLeft: 16, paddingRight: 16 }}>
      {topLabel && (
        <div
          className="and-type-caption-dark-medium"
          style={{ marginTop: 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {topLabel}
        </div>
      )}
      <div className="and-type-legacy-headline2" style={{ marginTop: topLabel ? 0 : 24 }}>
        {title}
      </div>
      {descriptionHtml && (
        <AndroidText html={descriptionHtml} className="and-type-body1-dark-medium" style={{ marginTop: 8 }} />
      )}
    </div>
  );
}

/** item_rating.xml — root marginTop 4 / marginBottom 20, 24dp bar, 16dp insets. */
function RatingBar({ rating, text }: { rating: number; text: string }) {
  const full = Math.round(rating);
  return (
    <div style={{ marginTop: 4, marginBottom: 20, display: 'flex', alignItems: 'center' }}>
      <div style={{ marginLeft: 16, height: 24, display: 'flex', alignItems: 'center', fontSize: 18, letterSpacing: 1 }}>
        <span aria-label={`${rating} stars`} style={{ color: 'var(--and-color-warning)' }}>
          {'★'.repeat(Math.min(5, full))}
          <span style={{ color: 'var(--and-color-divider)' }}>{'★'.repeat(Math.max(0, 5 - full))}</span>
        </span>
      </div>
      <div className="and-type-legacy-subtitle1" style={{ marginLeft: 16, color: 'var(--and-color-blue)' }}>
        {text}
      </div>
    </div>
  );
}

/** item_row.xml — 56dp fixed; divider start-aligned to the text (72dp with an icon). */
function Row({
  text,
  startIcon,
  endIcon,
  showDivider,
}: {
  text: string;
  startIcon: string | null;
  endIcon: 'chevron' | 'play';
  showDivider: boolean;
}) {
  const textStart = startIcon ? 72 : 16;
  return (
    <div style={{ position: 'relative', height: 56, display: 'flex', alignItems: 'center' }}>
      {startIcon && (
        <div
          style={{
            width: 40,
            height: 40,
            marginLeft: 8,
            padding: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--and-color-color-dark-medium)',
          }}
        >
          {startIcon === 'audio' ? '♪' : startIcon === 'image' ? '▤' : startIcon === 'document' ? '▦' : '↗'}
        </div>
      )}
      <div
        className="and-type-body1"
        style={{
          marginLeft: startIcon ? 24 : 16,
          marginRight: 16,
          flex: 1,
          minWidth: 0,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {text}
      </div>
      <div style={{ width: 24, height: 24, marginRight: 16, textAlign: 'center', color: 'var(--and-color-color-dark-medium)' }}>
        {endIcon === 'play' ? '▶' : '›'}
      </div>
      {showDivider && (
        <div
          style={{
            position: 'absolute',
            left: textStart,
            right: 0,
            bottom: 0,
            height: 1,
            background: 'var(--and-color-divider)',
          }}
        />
      )}
    </div>
  );
}

/** item_row_details.xml — 12dp above the label, 12dp below the value, divider inset 16. */
function RowDetails({ label, text, showDivider }: { label: string; text: string; showDivider: boolean }) {
  return (
    <div style={{ position: 'relative' }}>
      <div
        className="and-type-legacy-overline-dark-medium"
        style={{ marginLeft: 16, marginRight: 16, marginTop: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}
      </div>
      <div className="and-type-legacy-subtitle1" style={{ marginLeft: 16, marginRight: 16, marginBottom: 12, minHeight: 1 }}>
        {text}
      </div>
      {showDivider && (
        <div style={{ position: 'absolute', left: 16, right: 0, bottom: 0, height: 1, background: 'var(--and-color-divider)' }} />
      )}
    </div>
  );
}

/** item_section.xml — minHeight 26dp, Legacy.Headline2, margins from MarginItem. */
function Section({ text }: { text: string }) {
  return (
    <div className="and-type-legacy-headline2" style={{ ...marginStyle(SECTION_MARGINS), minHeight: 26 }}>
      {text}
    </div>
  );
}

/**
 * Text.Html rendered the way Android renders it: a flat run with newlines, no block
 * layout. `white-space: pre-wrap` is what makes the newlines the mapper produced
 * behave like the Spannable's.
 *
 * The HTML is sanitised in androidHtml() via DOMPurify against Android's own tag
 * allowlist, which is why the one react/no-danger exception in this tree lives here.
 */
function AndroidText({
  html,
  className,
  style,
}: {
  html: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const clean = androidHtml(html);
  return (
    <div
      className={className}
      style={{ whiteSpace: 'pre-wrap', ...style }}
      // eslint-disable-next-line react/no-danger -- sanitised in androidHtml() against Android's tag allowlist
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

export function ItemView({ item, ctx }: { item: Item; ctx: ItemContext }) {
  switch (item.kind) {
    case 'imageSlider':
      return <ImageSlider images={item.images} ctx={ctx} />;
    case 'detailsHeader':
      return (
        <DetailsHeader topLabel={item.topLabel} title={item.title} descriptionHtml={item.descriptionHtml} />
      );
    case 'ratingBar':
      return <RatingBar rating={item.rating} text={item.text} />;
    case 'row':
      return (
        <Row text={item.text} startIcon={item.startIcon} endIcon={item.endIcon} showDivider={item.showDivider} />
      );
    case 'rowDetails':
      return <RowDetails label={item.label} text={item.text} showDivider={item.showDivider} />;
    case 'section':
      return <Section text={item.text} />;
    default:
      return null;
  }
}

export { AndroidText, BODY_MARGINS };
